import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { RoundaboutCurrentRole, RoundaboutRoomUser } from "../roundabout-markings";
import { getOtherEditors } from "../roundabout-utilities";

type UseOtherStartedEditingProps = {
  roomUsers: RoundaboutRoomUser[];
  myRole: RoundaboutCurrentRole;
  mySub: string;
};

type OtherJustStartedEditing = { started: false } | { started: true; nicknames: string[] };

export const useOtherStartedEditing = ({
  myRole,
  mySub,
  roomUsers,
}: UseOtherStartedEditingProps): {
  otherStartedEditing: OtherJustStartedEditing;
  onCloseOtherStartedEditing: () => void;
} => {
  const [otherStartedEditing, setOtherStartedEditing] = useState<OtherJustStartedEditing>({
    started: false,
  });

  const onCloseOtherStartedEditing = useCallback(() => {
    setOtherStartedEditing({
      started: false,
    });
  }, []);

  const otherEditors = useMemo(() => getOtherEditors(roomUsers, mySub), [roomUsers, mySub]);

  // List of userIds that started editing, and we have acknowledged with the popup appearing
  // Initialise with the current list of users as those have been dealt with in first-exit sync
  const acknoledgedOtherEditors = useRef<string[]>(
    roomUsers.map((otherEditor) => otherEditor.userId),
  );

  useEffect(() => {
    // Removed editors
    // TODO this needs a grace period
    // If the user has left the room then clear them from other editor sessions acknoledgements
    // This means the popup will re-show if they return as an editor.
    // The grace period also needs to align with when the next viewer is made an editor
    acknoledgedOtherEditors.current = acknoledgedOtherEditors.current.filter((userId) =>
      otherEditors.some((otherEditor) => otherEditor.userId === userId),
    );

    // Panic 3; conceptually similar to someone already editing,
    // if someone starts editing. this 'resets' when the page is refreshed as technically
    // it is now you who is disrupting their sessions.
    // Watch for a person listed as an editor, who is not in our cache of acknowledged editors
    const newEditors = otherEditors.filter(
      (otherEditor) => !acknoledgedOtherEditors.current.includes(otherEditor.userId),
    );

    if (newEditors.length > 0 && myRole === "Editing") {
      setOtherStartedEditing({
        started: true,
        nicknames: newEditors.map((newEditor) => newEditor.nickname),
      });
      acknoledgedOtherEditors.current.push(...newEditors.map((newEditor) => newEditor.userId));
    }
  }, [otherEditors, myRole, mySub]);

  return {
    otherStartedEditing,
    onCloseOtherStartedEditing,
  };
};
